import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "lux-glam-cart";

export const CartContext = createContext();

function readStoredCart() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];

    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("readStoredCart error:", error);

    return [];
  }
}

function writeStoredCart(items) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error("writeStoredCart error:", error);
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => readStoredCart());

  useEffect(() => {
    writeStoredCart(items);
  }, [items]);

  const addItem = useCallback((item, quantity = 1) => {
    setItems((currentItems) => {
      const existingIndex = currentItems.findIndex(
        (currentItem) =>
          currentItem.productId === item.productId &&
          currentItem.variantId === item.variantId
      );

      if (existingIndex >= 0) {
        const nextItems = [...currentItems];
        const existingItem = nextItems[existingIndex];

        const nextQuantity = Math.min(
          existingItem.quantity + quantity,
          Math.max(Number(item.stock) || 0, 1)
        );

        nextItems[existingIndex] = {
          ...existingItem,
          quantity: nextQuantity,
        };

        return nextItems;
      }

      return [
        ...currentItems,
        {
          ...item,
          quantity: Math.max(1, Math.min(quantity, Math.max(Number(item.stock) || 0, 1))),
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((productId, variantId, quantity) => {
    setItems((currentItems) =>
      currentItems.map((item) => {
        if (item.productId !== productId || item.variantId !== variantId) {
          return item;
        }

        const maxQuantity = Math.max(Number(item.stock) || 0, 1);
        const nextQuantity = Math.max(1, Math.min(quantity, maxQuantity));

        return { ...item, quantity: nextQuantity };
      })
    );
  }, []);

  const removeItem = useCallback((productId, variantId) => {
    setItems((currentItems) =>
      currentItems.filter(
        (item) =>
          !(item.productId === productId && item.variantId === variantId)
      )
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const itemCount = useMemo(
    () => items.reduce((total, item) => total + item.quantity, 0),
    [items]
  );

  const subtotal = useMemo(
    () =>
      items.reduce(
        (total, item) => total + item.unitPrice * item.quantity,
        0
      ),
    [items]
  );

  const valueContext = useMemo(
    () => ({
      items,
      itemCount,
      subtotal,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
    }),
    [items, itemCount, subtotal, addItem, updateQuantity, removeItem, clearCart]
  );

  return (
    <CartContext.Provider value={valueContext}>
      {children}
    </CartContext.Provider>
  );
}
